/**
 * programs domain client — Programs as an organizational unit.
 *
 * Reads need `program:read`; every write needs `program:manage`. Types mirror
 * the API's program service views. There is no frontend-only duplicate of these
 * shapes anywhere: Admin, tests and any future worker usage consume this module.
 */
import { del, get, patch, post, put } from './transport.js';
/** `GET /programs` — server-side search, filter, sort and pagination. */
export async function list(query = {}, options) {
    return get('/programs', { ...options, query: { ...query } });
}
/** `GET /programs/:id` → the full operational object. */
export async function detail(id, options) {
    const body = await get(`/programs/${id}`, options);
    return body.program;
}
/** `POST /programs` (needs `program:manage`). */
export async function create(input, options) {
    const body = await post('/programs', input, options);
    return body.program;
}
/** `PATCH /programs/:id` (needs `program:manage`). */
export async function update(id, input, options) {
    const body = await patch(`/programs/${id}`, input, options);
    return body.program;
}
/**
 * `POST /programs/:id/retire` — ends a program's operational life and closes its
 * open assignments. Historical records keep referencing it.
 */
export async function retire(id, reason, options) {
    const body = await post(`/programs/${id}/retire`, { reason }, options);
    return body.program;
}
/** `POST /programs/:id/reactivate` — closed assignments stay closed. */
export async function reactivate(id, options) {
    const body = await post(`/programs/${id}/reactivate`, {}, options);
    return body.program;
}
/** `DELETE /programs/:id` — 409 unless nothing at all references the program. */
export async function remove(id, options) {
    await del(`/programs/${id}`, options);
}
/** `GET /programs/:id/workers` — `asOf` resolves membership as of a past date. */
export async function workers(id, query = {}, options) {
    const body = await get(`/programs/${id}/workers`, {
        ...options,
        query: { ...query },
    });
    return body.workers;
}
/** `POST /programs/:id/workers` — bulk add (needs `program:manage`). */
export async function addWorkers(id, input, options) {
    const body = await post(`/programs/${id}/workers`, input, options);
    return body.workers;
}
/**
 * `DELETE /programs/:id/workers` — bulk remove. This CLOSES the assignments; it
 * never deletes them, so past work stays attributable to the right program.
 */
export async function removeWorkers(id, input, options) {
    const body = await del(`/programs/${id}/workers`, {
        ...options,
        body: input,
    });
    return body.workers;
}
/** `GET /programs/:id/assignable-workers` — the "Add people" picker. */
export async function assignableWorkers(id, query = {}, options) {
    const body = await get(`/programs/${id}/assignable-workers`, {
        ...options,
        query: { ...query },
    });
    return body.workers;
}
/** `GET /programs/:id/sites`. */
export async function sites(id, options) {
    const body = await get(`/programs/${id}/sites`, options);
    return body.sites;
}
/**
 * `PUT /programs/:id/sites` — replaces the set. The Site screen edits the SAME
 * rows through `sites.setPrograms`, so the two surfaces cannot disagree.
 */
export async function setSites(id, input, options) {
    const body = await put(`/programs/${id}/sites`, input, options);
    return body.sites;
}
/**
 * `GET /programs/workers/:workerId` → `{ programs, scope }`.
 *
 * THE FRAME THE SERVER STATES SURVIVES THE CLIENT. The route's own comment
 * records that it carried the identity rule PER ROW rather than as an envelope
 * precisely because this package "returns `body.programs` and drops every
 * sibling key" — and `scope` is the one thing that cannot be carried per row.
 *
 * Prefer this over {@link forWorker} anywhere the absence of rows is rendered
 * as a statement about the worker: an empty `programs` under `scope:
 * 'programs'` is a withheld-as-absent inversion, which
 * `docs/withholding-contract.md` §4 ranks worst.
 */
export async function workerProgramRoster(workerId, query = {}, options) {
    return get(`/programs/workers/${workerId}`, {
        ...options,
        query: { ...query },
    });
}
/**
 * `GET /programs/workers/:workerId` → the rows ALONE.
 *
 * DROPS `scope`, deliberately and by name, so that a caller choosing it has
 * chosen to be unable to say whether an empty list means "in no programs" or
 * "in none you may be told about". Use {@link workerProgramRoster} unless the
 * rows are being rendered as themselves and nothing is being concluded from
 * their absence. Same shape, and the same reason, as `schedule.shifts` beside
 * `schedule.shiftRoster`.
 */
export async function forWorker(workerId, query = {}, options) {
    return (await workerProgramRoster(workerId, query, options)).programs;
}
//# sourceMappingURL=programs.js.map